import { createContext, ReactNode, useContext, useEffect, useReducer } from 'react';

import {
  defaultPaginatedItemState,
  paginatedItemsReducer,
} from 'context/PaginatedItems/paginatedItemsReducer';
import {
  PageResponse,
  PaginatedItems,
  PaginatedItemsActions,
} from 'context/PaginatedItems/types/PaginatedItems';
import useFetchData from 'hooks/useFetchData';

const PaginatedItemsContext = createContext<PaginatedItems<unknown> | undefined>(undefined);

type PaginatedItemsProviderProps = {
  pageableUrl: string;
  startingPageSize: number;
  pageSize: number;
  refreshKey?: boolean;
  children?: ReactNode;
};

const PaginatedItemsProvider = <T,>({
  pageableUrl,
  startingPageSize,
  pageSize,
  refreshKey,
  children,
}: PaginatedItemsProviderProps) => {
  const [paginatedItemState, paginatedItemDispatch] = useReducer(
    paginatedItemsReducer<T>,
    defaultPaginatedItemState
  );
  const { getData, isDataLoading } = useFetchData();
  const isInitialLoading = paginatedItemState.page === 0 && isDataLoading;
  const isLoadMoreLoading = paginatedItemState.page > 0 && isDataLoading;

  const getPageUrl = (page: number) => {
    const hasParams = pageableUrl.includes('?');
    const requestPageSize = page == 1 ? startingPageSize : pageSize;

    return `${pageableUrl}${hasParams ? '&' : '?'}page=${page}&pageSize=${requestPageSize}`;
  };

  const loadItemsAndDispatch = async (page: number, action: PaginatedItemsActions) => {
    await getData<PageResponse<T>>(getPageUrl(page), (pageResponse) => {
      paginatedItemDispatch({
        type: action,
        payload: pageResponse,
      });
    });
  };

  const loadMoreItems = async () => {
    loadItemsAndDispatch(paginatedItemState.page + 1, PaginatedItemsActions.LoadMore);
  };
  const paginatedItemsValues: PaginatedItems<T> = {
    items: paginatedItemState.items,
    totalElements: paginatedItemState.totalElements,
    totalPages: paginatedItemState.totalPages,
    currentItems: paginatedItemState.items.length,
    canLoadMore: paginatedItemState.page < paginatedItemState.totalPages,
    loadMore: loadMoreItems,
    isInitialLoading: isInitialLoading,
    isLoadMoreLoading: isLoadMoreLoading,
    pageNumber: paginatedItemState.page,
  };
  useEffect(() => {
    loadItemsAndDispatch(1, PaginatedItemsActions.InitialLoad);
  }, [pageableUrl, refreshKey]);

  return (
    <PaginatedItemsContext.Provider value={paginatedItemsValues}>
      {children}
    </PaginatedItemsContext.Provider>
  );
};

export const usePaginatedItemsContext: () => PaginatedItems<unknown> = () => {
  const paginatedItemsContext = useContext(PaginatedItemsContext);

  if (!paginatedItemsContext) {
    throw new Error('usePaginatedItemsContext must be used within PaginatedItemsProvider');
  }

  return paginatedItemsContext;
};

export default PaginatedItemsProvider;
